/**
 * One raid profile editor, used by both the manual and the automatic page.
 *
 * `mode="manual"` edits the single profile behind the admin Summon button.
 * `mode="auto"` edits one profile per server, because Modded Demise allows
 * Lv 1-255 while Revival is vanilla-capped at 65 — a shared row could never
 * suit both.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Save, Info, AlertTriangle, Eye, Copy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { RaidPrizeLadder } from "@/components/admin/RaidPrizeLadder";
import {
  PassivePicker, PassiveRankIcon, TIER_COLOR, usePassiveIconMap,
} from "@/components/admin/PassivePicker";

const NO_SPIN =
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none " +
  "[&::-webkit-inner-spin-button]:appearance-none";
const NUM = `input input-sm w-full text-[11px] ${NO_SPIN}`;

const ENCOUNTER_NUMS = [
  ["spawn_scale", "Size", "×", 0.1, 5, 0.1],
  ["damage_taken_multiplier", "Dmg taken", "×", 0.1, 10, 0.1],
  ["damage_dealt_multiplier", "Dmg dealt", "×", 0.1, 10, 0.1],
  ["capturable_at_health_percent", "Catchable at", "% HP", 0, 100, 1],
];

const TEMPLATE_NUMS = [
  ["rank", "Star rank", 0, 20],
  ["rank_hp", "Soul HP", 0, 1020],
  ["rank_attack", "Soul Atk", 0, 1020],
  ["rank_defence", "Soul Def", 0, 1020],
  ["rank_craft_speed", "Soul Craft", 0, 1020],
  ["trust", "Trust", 0, 10],
  ["iv_cap", "IV cap", 1, 255],
  ["soul_cap", "Soul cap", 0, 1020],
];

export function RaidProfileEditor({ mode, server, onLoaded }) {
  const toast = useToast();
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const qs = mode === "auto" ? `?mode=auto&server=${encodeURIComponent(server)}` : "?mode=manual";

  const load = useCallback(() => {
    if (mode === "auto" && !server) return;
    setPreview(null);
    api
      .get(`/admin/raids/profile${qs}`)
      .then(({ data }) => {
        setCfg(data.config);
        setMeta(data);
        onLoaded?.(data);
      })
      .catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, server, qs, toast]);

  useEffect(load, [load]);

  const patch = (key, value) => setCfg((c) => ({ ...c, [key]: value }));
  const patchT = (key, value) =>
    setCfg((c) => ({ ...c, template: { ...c.template, [key]: value } }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/raids/profile${qs}`, cfg);
      setCfg(data.config);
      toast.success(mode === "auto" ? `Saved ${server}` : "Saved manual defaults");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/raids/profile/reset${qs}`);
      setCfg(data.config);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  /** Show both files before anything fires — PalDefender only complains
      afterwards, in a log file. */
  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/raids/profile/preview${qs}`, {
        ...cfg, preview_passives: cfg.template?.passives || [],
      });
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyFrom = async (value) => {
    if (!value) return;
    const [fm, fs] = value.split("::");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/raids/profile/copy", {
        from_mode: fm,
        from_server: fs || null,
        to_mode: mode,
        to_server: mode === "auto" ? server : null,
      });
      setCfg(data.config);
      toast.success(`Copied from ${data.copied_from}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!cfg || !meta) return null;

  const meterOff = !cfg.use_damage_meter;
  const d = cfg.prizes?.Default || {};
  const defaultEmpty =
    !d.exp && !d.tech && !d.ancient_tech &&
    !(d.items || []).length && !(d.eggs || []).length && !(d.bonus || []).length;
  const thisServer = meta.servers?.find((s) => s.name === server);
  const cap = mode === "auto" ? thisServer?.level_cap : 255;

  const copyOptions = [
    { value: "manual::", label: "Manual defaults" },
    ...(meta.servers || []).map((s) => ({
      value: `auto::${s.name}`,
      label: `Automatic — ${s.name}`,
    })),
  ].filter((o) => o.value !== (mode === "auto" ? `auto::${server}` : "manual::"));

  return (
    <div className="space-y-4" data-testid={`profile-editor-${mode}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <ul className="space-y-1 max-w-3xl">
          {(meta.notes || []).map((n) => (
            <li key={n} className="text-[11px] text-[var(--muted)] flex gap-1.5">
              <Info size={11} className="mt-0.5 flex-shrink-0 text-[var(--teal)]" />
              {n}
            </li>
          ))}
        </ul>
        <div className="flex gap-2 flex-wrap">
          <select
            className="input input-sm text-[11px]"
            defaultValue=""
            onChange={(e) => copyFrom(e.target.value)}
            disabled={busy}
            data-testid={`copy-from-${mode}`}
          >
            <option value="">Copy profile from…</option>
            {copyOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button onClick={doPreview} disabled={busy}
            className="btn btn-sm btn-secondary flex items-center gap-1.5"
            data-testid={`preview-${mode}`}>
            <Eye size={12} /> Preview JSON
          </button>
          <button onClick={reset} disabled={busy}
            className="btn btn-sm btn-secondary flex items-center gap-1.5"
            data-testid={`reset-${mode}`}>
            <RefreshCw size={12} /> Defaults
          </button>
          <button onClick={save} disabled={busy}
            className="btn btn-sm flex items-center gap-1.5"
            data-testid={`save-${mode}`}>
            <Save size={12} /> {mode === "auto" ? "Save for this server" : "Save as defaults"}
          </button>
        </div>
      </div>

      <section className="glass-card p-4">
        <h4 className="text-xs uppercase tracking-wider text-[var(--bone)] mb-3">
          Encounter
        </h4>
        <div className="flex flex-wrap gap-4">
          {[
            ["enabled", "Use summon system"],
            ["use_damage_meter", "Damage meter + rank ladder"],
            ["disable_ai", "Disable boss AI"],
            ["uncapturable", "Cannot be caught"],
            ["disable_statuses", "Status immunities"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!!cfg[key]}
                onChange={(e) => patch(key, e.target.checked)}
                className="accent-[var(--teal)]"
                data-testid={`flag-${mode}-${key}`} />
              {label}
            </label>
          ))}
          {ENCOUNTER_NUMS.map(([key, label, suffix, min, max, step]) => (
            <label key={key} className="flex items-center gap-1.5 text-xs">
              {label}
              <span className="inline-block w-16">
                <input type="number" step={step} min={min} max={max}
                  value={cfg[key] ?? ""}
                  onChange={(e) => patch(key, parseFloat(e.target.value) || 0)}
                  className={NUM} data-testid={`num-${mode}-${key}`} />
              </span>
              {suffix}
            </label>
          ))}
        </div>

        {meterOff && (
          <p className="mt-3 text-[11px] text-amber-300 flex gap-1.5 leading-snug"
            data-testid={`meter-warning-${mode}`}>
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              With the damage meter off, <strong>ranks 1-3 are never used</strong>.
              PalDefender grants <strong>Default</strong> to every player online when
              the raid ends — including anyone who never touched the boss.
              {defaultEmpty && " Default is currently empty, so nobody would receive anything."}
            </span>
          </p>
        )}

        {cfg.disable_statuses && (
          <div className="mt-3">
            <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1">
              Statuses switched off
            </p>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {(meta.statuses_available || []).map((s) => {
                const on = (cfg.statuses || []).includes(s);
                return (
                  <label key={s} className="flex items-center gap-1 text-[11px] cursor-pointer">
                    <input type="checkbox" checked={on}
                      onChange={(e) =>
                        patch("statuses", e.target.checked
                          ? [...(cfg.statuses || []), s]
                          : (cfg.statuses || []).filter((x) => x !== s))}
                      className="accent-[var(--teal)]"
                      data-testid={`status-${mode}-${s}`} />
                    {s}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <section className="glass-card p-4">
        <h4 className="text-xs uppercase tracking-wider text-[var(--bone)] mb-1">
          The boss itself
        </h4>
        <p className="text-[11px] text-[var(--muted)] mb-3">
          These were welded into the code and could not be changed from anywhere.
          {mode === "auto" && cap && (
            <> Scheduled raids on this server are capped at <strong>level {cap}</strong>.</>
          )}
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-[10px] text-[var(--muted)]">
            IVs (HP / Atk / Def)
            <span className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <input key={i} type="number" min="0" max={cfg.template?.iv_cap ?? 100}
                  value={cfg.template?.talents?.[i] ?? 0}
                  onChange={(e) => {
                    const next = [...(cfg.template?.talents || [0, 0, 0])];
                    next[i] = parseInt(e.target.value, 10) || 0;
                    patchT("talents", next);
                  }}
                  className={`${NUM} w-14`} data-testid={`iv-${mode}-${i}`} />
              ))}
            </span>
          </label>
          {TEMPLATE_NUMS.map(([key, label, min, max]) => (
            <label key={key} className="text-[10px] text-[var(--muted)]">
              {label}
              <input type="number" min={min} max={max}
                value={cfg.template?.[key] ?? 0}
                onChange={(e) => patchT(key, parseInt(e.target.value, 10) || 0)}
                className={`${NUM} w-16`} data-testid={`tmpl-${mode}-${key}`} />
            </label>
          ))}
          <label className="text-[10px] text-[var(--muted)]">
            Gender
            <select value={cfg.template?.gender || "None"}
              onChange={(e) => patchT("gender", e.target.value)}
              className={`${NUM} w-20`} data-testid={`tmpl-${mode}-gender`}>
              {(meta.genders || ["Male", "Female", "None"]).map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </label>
          <label className="text-[10px] text-[var(--muted)]">
            Nickname
            <input type="text" maxLength={32} value={cfg.template?.nick_name || ""}
              onChange={(e) => patchT("nick_name", e.target.value)}
              className={`${NUM} w-32 text-left`} placeholder="(none)"
              data-testid={`tmpl-${mode}-nick`} />
          </label>
          <label className="text-[10px] text-[var(--muted)]">
            Attack moves (comma separated)
            <input type="text" value={(cfg.template?.active_skills || []).join(", ")}
              onChange={(e) => patchT("active_skills",
                e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              className={`${NUM} w-56 text-left`} placeholder="(game default)"
              data-testid={`tmpl-${mode}-skills`} />
          </label>
          <div className="text-[10px] text-[var(--muted)]">
            Passives
            <div className="mt-1">
              <PassivePicker
                selected={cfg.template?.passives || []}
                onChange={(p) => patchT("passives", p)}
                slug={`profile-${mode}`}
              />
              <SelectedPassives names={cfg.template?.passives || []} mode={mode} />
            </div>
          </div>
          {[["awakened", "Awakened aura"], ["is_lucky", "Lucky"], ["is_boss", "Alpha boss"]].map(
            ([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={!!cfg.template?.[key]}
                  onChange={(e) => patchT(key, e.target.checked)}
                  className="accent-[var(--teal)]"
                  data-testid={`tmpl-${mode}-${key}`} />
                {label}
              </label>
            ),
          )}
        </div>
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1">
            Work suitability bonuses
          </p>
          <div className="flex flex-wrap gap-2">
            {(meta.work_types || []).map((w) => (
              <label key={w} className="text-[10px] text-[var(--muted)]">
                {w}
                <input type="number" min="0" max="5"
                  value={cfg.template?.work_suits?.[w] ?? 0}
                  onChange={(e) => {
                    const next = { ...(cfg.template?.work_suits || {}) };
                    const v = parseInt(e.target.value, 10) || 0;
                    if (v > 0) next[w] = v; else delete next[w];
                    patchT("work_suits", next);
                  }}
                  className={`${NUM} w-12`} data-testid={`work-${mode}-${w}`} />
              </label>
            ))}
          </div>
        </div>
      </section>

      <section className="glass-card p-4">
        <h4 className="text-xs uppercase tracking-wider text-[var(--bone)] mb-3">
          Rewards
        </h4>
        <RaidPrizeLadder
          prizes={cfg.prizes || {}}
          onChange={(p) => patch("prizes", p)}
          testPrefix={`prize-${mode}`}
        />
      </section>

      {preview && (
        <section className="glass-card p-4" data-testid={`preview-pane-${mode}`}>
          <h4 className="text-xs uppercase tracking-wider text-[var(--bone)] mb-2">
            Exactly what gets written
          </h4>
          {(preview.warnings || []).map((w) => (
            <p key={w} className="text-[11px] text-amber-300 flex gap-1.5 mb-2">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />{w}
            </p>
          ))}
          <div className="grid gap-3 lg:grid-cols-2">
            {[["PalTemplate", preview.pal_template], ["PalSummon", preview.pal_summon]].map(
              ([label, blob]) => (
                <div key={label}>
                  <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1 flex items-center gap-1">
                    <Copy size={10} /> {label}
                  </p>
                  <pre className="text-[10px] leading-snug bg-black/40 rounded p-2 overflow-auto max-h-80">
                    {JSON.stringify(blob, null, 2)}
                  </pre>
                </div>
              ),
            )}
          </div>
        </section>
      )}
    </div>
  );
}

/** The profile's pinned passives, with their in-game rank arrows so the row
    reads like the Pal Builder. Empty = the spawner rolls 4 at random. */
function SelectedPassives({ names, mode }) {
  const byName = usePassiveIconMap();
  if (!names.length) {
    return (
      <p className="text-[10px] text-[var(--dim)] mt-1" data-testid={`tmpl-${mode}-passives-auto`}>
        Auto — 4 random passives from the raid category pool.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid={`tmpl-${mode}-passives-list`}>
      {names.map((n) => (
        <span key={n}
          className={`text-[10px] px-1.5 py-0.5 rounded bg-white/5 flex items-center gap-1 ${TIER_COLOR[byName[n]?.tier] || "text-[var(--bone)]"}`}>
          <PassiveRankIcon tier={byName[n]?.tier} icon={byName[n]?.icon} size={11} />
          {n}
        </span>
      ))}
    </div>
  );
}
